/**
 * @fileoverview Match3 Result Overlay Component
 * @description Game over screen with results and actions
 */

import React, { useEffect, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  Animated,
  TouchableOpacity,
  Modal,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColors } from "@/providers/ThemeProvider";
import { Match3Result } from "../types";
import LottieView from "lottie-react-native";
import { getLottieSource } from "@/lib/lottie-registry";

interface Match3ResultOverlayProps {
  visible: boolean;
  result: Match3Result | null;
  mode: "free" | "weekend";
  lottieKey?: string;
  onPlayAgain: () => void;
  onExit: () => void;
}

/**
 * Result overlay shown at game end
 */
export const Match3ResultOverlay: React.FC<Match3ResultOverlayProps> = ({
  visible,
  result,
  mode,
  lottieKey,
  onPlayAgain,
  onExit,
}) => {
  const colors = useThemeColors();
  const slideAnim = useRef(new Animated.Value(100)).current;
  const fadeAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (visible) {
      Animated.parallel([
        Animated.timing(slideAnim, {
          toValue: 0,
          duration: 400,
          useNativeDriver: true,
        }),
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 300,
          useNativeDriver: true,
        }),
      ]).start();
    } else {
      slideAnim.setValue(100);
      fadeAnim.setValue(0);
    }
  }, [visible, slideAnim, fadeAnim]);

  if (!visible || !result) return null;

  const lottieSource = lottieKey ? getLottieSource(lottieKey) : null;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="none"
      statusBarTranslucent
    >
      <Animated.View
        style={[
          styles.overlay,
          { backgroundColor: "rgba(0,0,0,0.7)", opacity: fadeAnim },
        ]}
      >
        <Animated.View
          style={[
            styles.card,
            {
              backgroundColor: colors.surface,
              transform: [{ translateY: slideAnim }],
            },
          ]}
        >
          {/* Lottie Animation */}
          {lottieSource && (
            <View style={styles.lottieContainer}>
              <LottieView
                source={lottieSource}
                autoPlay
                loop={false}
                style={styles.lottie}
              />
            </View>
          )}

          {/* Title */}
          <Text style={[styles.title, { color: colors.text }]}>
            {result.isNewFamilyRecord
              ? "🏆 Nieuw Familie Record!"
              : result.isNewHighScore
              ? "⭐ Nieuw Persoonlijk Record!"
              : "Tijd is op!"}
          </Text>

          {/* Score */}
          <View style={styles.scoreContainer}>
            <Text style={[styles.scoreLabel, { color: colors.textSecondary }]}>
              Score
            </Text>
            <Text style={[styles.scoreValue, { color: colors.primary }]}>
              {result.score.toLocaleString()}
            </Text>
          </View>

          {/* Stats Grid */}
          <View style={styles.statsGrid}>
            <View style={styles.statItem}>
              <Ionicons name="swap-horizontal" size={20} color={colors.primary} />
              <Text style={[styles.statValue, { color: colors.text }]}>
                {result.movesMade}
              </Text>
              <Text style={[styles.statLabel, { color: colors.textSecondary }]}>
                Zetten
              </Text>
            </View>

            <View style={styles.statItem}>
              <Ionicons name="flash" size={20} color={colors.warning} />
              <Text style={[styles.statValue, { color: colors.text }]}>
                {result.maxCombo}x
              </Text>
              <Text style={[styles.statLabel, { color: colors.textSecondary }]}>
                Max Combo
              </Text>
            </View>

            <View style={styles.statItem}>
              <Ionicons name="time" size={20} color={colors.success} />
              <Text style={[styles.statValue, { color: colors.text }]}>
                +{result.timeBonusesEarned}s
              </Text>
              <Text style={[styles.statLabel, { color: colors.textSecondary }]}>
                Bonus Tijd
              </Text>
            </View>
          </View>

          {/* Weekend Points */}
          {mode === "weekend" && result.pointsEarned > 0 && (
            <View
              style={[
                styles.pointsBox,
                { backgroundColor: colors.successLight },
              ]}
            >
              <Ionicons name="trophy" size={24} color={colors.success} />
              <Text style={[styles.pointsValue, { color: colors.success }]}>
                +{result.pointsEarned} punten
              </Text>
              <Text style={[styles.pointsLabel, { color: colors.success }]}>
                voor je team
              </Text>
            </View>
          )}

          {/* Milestones */}
          {result.milestonesAchieved.length > 0 && (
            <View style={styles.milestonesContainer}>
              <Text style={[styles.milestonesTitle, { color: colors.text }]}>
                Mijlpalen behaald:
              </Text>
              {result.milestonesAchieved.map((milestone, index) => (
                <View key={index} style={styles.milestoneRow}>
                  <Ionicons name="checkmark-circle" size={16} color={colors.success} />
                  <Text style={[styles.milestoneText, { color: colors.textSecondary }]}>
                    {milestone}
                  </Text>
                </View>
              ))}
            </View>
          )}

          {/* Action Buttons */}
          <View style={styles.actions}>
            <TouchableOpacity
              style={[styles.button, styles.secondaryButton, { borderColor: colors.border }]}
              onPress={onExit}
              accessibilityLabel="Terug naar lobby"
              accessibilityRole="button"
            >
              <Ionicons name="arrow-back" size={20} color={colors.text} />
              <Text style={[styles.buttonText, { color: colors.text }]}>
                Terug
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.button, styles.primaryButton, { backgroundColor: colors.primary }]}
              onPress={onPlayAgain}
              accessibilityLabel="Opnieuw spelen"
              accessibilityRole="button"
            >
              <Ionicons name="refresh" size={20} color="#FFFFFF" />
              <Text style={[styles.buttonText, { color: "#FFFFFF" }]}>
                Opnieuw
              </Text>
            </TouchableOpacity>
          </View>
        </Animated.View>
      </Animated.View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
  card: {
    width: "100%",
    maxWidth: 400,
    borderRadius: 20,
    padding: 24,
    alignItems: "center",
  },
  lottieContainer: {
    width: 120,
    height: 120,
    marginBottom: 8,
  },
  lottie: {
    width: "100%",
    height: "100%",
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 16,
  },
  scoreContainer: {
    alignItems: "center",
    marginBottom: 20,
  },
  scoreLabel: {
    fontSize: 14,
    fontWeight: "500",
    marginBottom: 4,
  },
  scoreValue: {
    fontSize: 48,
    fontWeight: "bold",
  },
  statsGrid: {
    flexDirection: "row",
    justifyContent: "space-around",
    width: "100%",
    marginBottom: 20,
  },
  statItem: {
    alignItems: "center",
    gap: 4,
  },
  statValue: {
    fontSize: 18,
    fontWeight: "bold",
  },
  statLabel: {
    fontSize: 12,
  },
  pointsBox: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 12,
    marginBottom: 16,
  },
  pointsValue: {
    fontSize: 20,
    fontWeight: "bold",
  },
  pointsLabel: {
    fontSize: 14,
  },
  milestonesContainer: {
    width: "100%",
    marginBottom: 20,
  },
  milestonesTitle: {
    fontSize: 14,
    fontWeight: "600",
    marginBottom: 8,
  },
  milestoneRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 4,
  },
  milestoneText: {
    fontSize: 13,
  },
  actions: {
    flexDirection: "row",
    gap: 12,
    width: "100%",
  },
  button: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    paddingVertical: 14,
    borderRadius: 12,
  },
  primaryButton: {},
  secondaryButton: {
    borderWidth: 1,
  },
  buttonText: {
    fontSize: 16,
    fontWeight: "600",
  },
});

export default Match3ResultOverlay;

